import { DataTable } from "@bcc/components/data-table/data-table";
import type { DataTableRow, GroupedRowActionHelpers } from "@bcc/components/data-table/data-table.types";
import type { BulkAction } from "@client-shared";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { PurchaseOrder } from "@shared/types";
import type { Row } from "@tanstack/react-table";
import { RowSelectionState, SortingState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_ORDER_STATUS,
  DEFAULT_ORDERS_PAGE_SIZE,
  DISABLED_ORDER_STATUSES,
  MAX_PURCHASE_ORDER_CONNECTIONS_LIMIT,
  ORDER_COLUMN_IDS,
  PENDING_VALIDATION_STATUS,
} from "../constants";
import { exportOrdersToCsv } from "../utils/orders-export";
import { GroupOrderSelectionPopover } from "./group-order-selection-popover";
import { getOrderColumns } from "./orders-columns";
import { setOrdersDataTableContainerTestId } from "./orders-data-table.utils";
import { compareByFulfillmentThenCreatedDesc } from "./orders-sort.utils";

interface OrdersDataTableProps {
  readonly orders: DataTableRow<PurchaseOrder>[];
  readonly onOrderClick?: (order: PurchaseOrder) => void;
  readonly onBatchClick?: (batchId: string) => void;
  readonly onCreateBatch?: (orders: PurchaseOrder[]) => void;
  readonly isLoading?: boolean;
  readonly globalFilter?: string;
  readonly exportToCSV?: boolean;
  readonly selectable?: boolean;
  /** Selection is cleared whenever this changes. Leave it stable to keep the selection across data changes. */
  readonly selectionResetKey?: string;
  readonly clearCache?: () => void;
  readonly testId?: string;
}

const getOrderRowId = (order: PurchaseOrder) => order.purchaseOrderId;

const DEFAULT_SORTING: SortingState = [
  { id: ORDER_COLUMN_IDS.EXPECTED_FULFILLMENT_DATE, desc: true },
  { id: ORDER_COLUMN_IDS.CREATED_AT_WITH_TIME, desc: true },
];

const DEFAULT_PAGINATION = {
  pageIndex: 0,
  pageSize: DEFAULT_ORDERS_PAGE_SIZE,
} as const;

const COLUMN_VISIBILITY = {
  [ORDER_COLUMN_IDS.CREATED_AT_STRING]: false,
  [ORDER_COLUMN_IDS.CREATED_AT_WITH_TIME]: false,
  [ORDER_COLUMN_IDS.EXPECTED_FULFILLMENT_DATE]: false,
  [ORDER_COLUMN_IDS.SHIPPING_ADDRESS]: false,
};

export function OrdersDataTable({
  orders,
  onOrderClick,
  onBatchClick,
  onCreateBatch,
  isLoading = false,
  globalFilter,
  exportToCSV,
  selectable = true,
  selectionResetKey,
  clearCache: _clearCache,
  testId,
}: OrdersDataTableProps) {
  const { t } = useTranslation();
  const [{ currentVendor, customerAccountCarrierDetails, currencyObject }] = useVendorContext();
  // `shipTo` always includes the vendor's own home country, so a length of 1
  // means domestic-only — cross-border requires 2+.
  const showShipToCountry = (currentVendor?.properties?.shipTo?.length ?? 0) > 1;

  // Rows are keyed by purchaseOrderId, so a selection survives filter changes that keep the same
  // orders visible (e.g. All -> Waiting). Controlled so the parent can clear it when it no longer applies.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [appliedResetKey, setAppliedResetKey] = useState(selectionResetKey);
  if (appliedResetKey !== selectionResetKey) {
    setAppliedResetKey(selectionResetKey);
    setRowSelection({});
  }

  const handleExport = useCallback((filteredRows: unknown[]) => exportOrdersToCsv(filteredRows, t, currencyObject), [t, currencyObject]);

  // Apply status-based isDisabled override on top of provider's beanzConnectLabelPrintEnabled check.
  // Also pre-sort by fulfillment date: TanStack groups rows before sorting, and a
  // group's row is seeded from the first leaf it encounters in `data` order — so
  // without a pre-sorted input, day-groups end up ordered by an arbitrary member
  // instead of DEFAULT_SORTING's intended newest-fulfillment-first order.
  const rows: DataTableRow<PurchaseOrder>[] = useMemo(
    () =>
      orders
        .map((order) => ({
          ...order,
          isDisabled: Boolean(order.isDisabled) || (order.orderStatus !== undefined && DISABLED_ORDER_STATUSES.has(order.orderStatus)),
          tone: order.orderStatus === PENDING_VALIDATION_STATUS ? "subdued" : order.tone,
        }))
        .sort(compareByFulfillmentThenCreatedDesc),
    [orders]
  );

  const columns = useMemo(
    () =>
      getOrderColumns({
        onOrderClick,
        onBatchClick,
        carrierDetails: customerAccountCarrierDetails ?? [],
        t,
        showShipToCountry,
      }),
    [onOrderClick, onBatchClick, customerAccountCarrierDetails, t, showShipToCountry]
  );

  const groupedRowActions = useCallback(
    (row: Row<DataTableRow<PurchaseOrder>>, helpers: GroupedRowActionHelpers<PurchaseOrder>) => (
      <GroupOrderSelectionPopover row={row} helpers={helpers} />
    ),
    []
  );

  const bulkActions: BulkAction[] = useMemo(
    () => [
      {
        label: t("views.orders.bulkActions.createBatch"),
        onClick: (selection: RowSelectionState) => {
          const selected = rows.filter((row) => selection[row.purchaseOrderId] === true);
          onCreateBatch?.(selected);
        },
        state: (selection: RowSelectionState) => {
          const selectedOrders = rows.filter((row) => selection[row.purchaseOrderId] === true);
          if (selectedOrders.length === 0) return "disabled";
          const allValid =
            selectedOrders.every((o) => o.beanzConnectLabelPrintEnabled) &&
            selectedOrders.every((o) => o.orderStatus === DEFAULT_ORDER_STATUS);
          return allValid ? undefined : "disabled";
        },
      },
    ],
    [rows, t, onCreateBatch]
  );

  return (
    <div data-testid={testId ?? setOrdersDataTableContainerTestId()} className="**:data-[testid='DataTable-FilterBar']:hidden">
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        emptyMessage={t("views.orders.emptyState")}
        globalFilter={globalFilter}
        selectable={selectable}
        maxSelectableRows={MAX_PURCHASE_ORDER_CONNECTIONS_LIMIT}
        getRowId={getOrderRowId}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        selectAllLabel={selectable ? t("views.orders.selectAllOpenOrders") : undefined}
        bulkActions={selectable ? bulkActions : undefined}
        grouping={[ORDER_COLUMN_IDS.CREATED_AT]}
        showGroupCount
        sorting={DEFAULT_SORTING}
        columnVisibility={COLUMN_VISIBILITY}
        pagination={DEFAULT_PAGINATION}
        exportable
        exportFilename="orders"
        onExportRows={handleExport}
        exportToCSV={exportToCSV}
        groupedRowActions={groupedRowActions}
      />
    </div>
  );
}
